import { EventEmitter, h, Component, Prop, Event, Host, Element, Method, AttachInternals } from '@stencil/core';

import { isIOS, isSafari } from '../../../services/utils';

const BUTTON_APPEARANCES = ['contained', 'outlined', 'text', 'icon'];

/**
 * @part button - The `<button></button>` HTML element used under the hood.
 * @part link - The `<a></a>` HTML element used under the hood (valid only when `link-href` have been set).
 */
@Component({
  tag: 'pk-button',
  styleUrl: './scss/pk-button.scss',
  formAssociated: true,
  shadow: {
    delegatesFocus: true,
  },
})
export class PkButton {
  // Own Properties
  // ====================

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkButtonElement;
  @AttachInternals() internals!: ElementInternals;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  // Public Property API
  // ========================

  /** The base styling to apply to the button */
  @Prop({ reflect: true }) appearance?: 'contained' | 'outlined' | 'text' | 'icon' = 'contained';

  /** Defines a string value that labels the interactive button element used under the hook */
  @Prop({ reflect: true }) ariaLabel: string | null;

  /** Set if the button is disabled */
  @Prop({ reflect: true }) disabled?: boolean = false;

  /** The value of this attribute must be the id of the `form`, in the same document. */
  @Prop({ reflect: true }) formId?: string;

  /** Set high contrast color to outlined, text and icon types button */
  @Prop({ mutable: true }) highContrast?: boolean;

  /** The href of the link. If is empty, the link button is disabled */
  @Prop({ reflect: true }) linkHref?: string;

  /**
   * Specifies where to display the linked URL, as the name for a browsing context (a tab, window, or `<iframe>`).
   * Details: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-target
   */
  @Prop({ reflect: true }) linkTarget?: string;

  /** Specifies the relationship between the current page and the linked page */
  @Prop({ reflect: true }) linkRel?: string;

  /** If `true` it will apply the soft style variation for an icon button (ignored if `appearance!="icon"`) */
  @Prop({ reflect: true }) soft: boolean = false;

  /** The default behavior of the button */
  @Prop({ reflect: true }) type?: 'submit' | 'reset' | 'button' = 'button';

  /** Set button width in px; string should be in px; applies to type 'contained' and 'outlined' only */
  @Prop({ mutable: true }) width?: string;

  // Prop lifecycle events
  // =======================

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Handler to be called on button click */
  @Event() pkClick: EventEmitter;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentWillLoad() {
    if (this.appearance && !BUTTON_APPEARANCES.includes(this.appearance)) {
      console.warn(`[Pk-Button]: 'appearance' property should be one of ${String(BUTTON_APPEARANCES)}`);
    }
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  /**
   * Sets focus on the native `<a>` or `<button>` HTML element used under the hood.
   * Use this method instead of the global `element.focus()`.
   */
  @Method()
  async setFocus(): Promise<void> {
    const buttons = this.el.shadowRoot.querySelectorAll('a[href], button');
    if (!buttons?.length) return;

    Array.from(buttons).forEach((btn: HTMLElement) => {
      btn.focus();
    });
  }

  // Local methods
  // Internal business logic.
  // ==========================

  private handleOnClick = (e: Event) => {
    const {
      disabled,
      type,
      internals: { form },
    } = this;

    // Early return if disabled
    if (disabled) {
      this.preventDefault(e);
      return;
    }

    // Emit click event and check if it was prevented
    const clickEvent = this.pkClick.emit(e);
    if (clickEvent.defaultPrevented) {
      this.preventDefault(e);
      return;
    }

    // Handle form actions
    if (form && type === 'submit') {
      form.requestSubmit();
    }

    if (form && type === 'reset') {
      form.reset();
    }

    // Fix focus behavior for iOS/Safari
    if (isIOS() || isSafari()) {
      const buttonElement = this.el.shadowRoot.querySelector('.pk-button') as HTMLElement;
      if (buttonElement) buttonElement.focus();
    }
  };

  private preventDefault = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
  };

  private get isContainedOrOutlined(): boolean {
    return this.appearance === 'contained' || this.appearance === 'outlined';
  }

  private get isSoftButton(): boolean {
    return this.soft && this.appearance === 'icon';
  }

  private get isLink(): boolean {
    return !!this.linkHref;
  }

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const style = {
      ...(this.width && this.isContainedOrOutlined && { '--button--width': `${this.width}px` }),
    };
    const TagElem = this.isLink ? 'a' : 'button';

    return (
      <Host
        class={{
          'adaptive-width': !this.width && this.isContainedOrOutlined,
          'fixed-width': this.width && this.isContainedOrOutlined,
          'high-contrast': this.appearance !== 'contained' && this.highContrast,
        }}
        style={{ ...style }}
      >
        <TagElem
          class={{
            'pk-button no-underline': true,
            [`pk-button--${String(this.appearance)}`]: BUTTON_APPEARANCES.includes(this.appearance),
            'is-soft': this.isSoftButton,
            disabled: this.disabled,
          }}
          aria-label={this.ariaLabel}
          aria-disabled={this.disabled ? 'true' : 'false'}
          href={this.isLink ? this.linkHref : undefined}
          rel={this.isLink ? this.linkRel : undefined}
          target={this.isLink ? this.linkTarget : undefined}
          role={this.isLink ? 'link' : 'button'}
          disabled={this.disabled}
          onClick={this.handleOnClick}
          form={this.formId}
          type={this.type}
          part={this.isLink ? 'link' : 'button'}
          tabIndex={this.disabled ? -1 : 0}
        >
          <slot />
        </TagElem>
      </Host>
    );
  }
}
